import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  InternalAxiosRequestConfig,
} from 'axios';
import toast from 'react-hot-toast';
import type { ApiResponse } from '@court-sync/shared';
import { auth } from '../config/firebase';
import { API_BASE_URL } from '../config/api';
import { getImpersonationRequestHeaders } from '../utils/impersonationHeaders';
import { clearStaleImpersonationIfNeeded } from '../utils/impersonationRecovery';
import { isPublicBrowsePath } from '../lib/isPublicBrowsePath';
import { isPlatformOnboardingPath } from '../utils/emailVerificationRedirect';

export class ApiError extends Error {
  readonly status: number;
  readonly code?: string;
  /** Per-item validation messages some endpoints attach alongside the top-level `error` string. */
  readonly details?: string[];
  /** Raw API response body when the request failed (e.g. branded-subdomain block payload). */
  readonly responseData?: unknown;

  constructor(
    message: string,
    status: number,
    code?: string,
    details?: string[],
    responseData?: unknown
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
    this.responseData = responseData;
  }
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

function extractErrorMessage(error: AxiosError<ApiResponse<unknown>>): string {
  const data = error.response?.data;
  if (data && typeof data === 'object' && 'error' in data && typeof data.error === 'string') {
    return data.error;
  }
  if (error.response?.status === 429) {
    return 'Too many requests. Please wait a moment and try again.';
  }
  if (error.response?.status === 401) {
    return 'Your session has expired. Please sign in again.';
  }
  return error.message || 'Request failed';
}

function extractErrorDetails(error: AxiosError<ApiResponse<unknown>>): string[] | undefined {
  const data = error.response?.data as { details?: unknown } | undefined;
  if (data && Array.isArray(data.details) && data.details.every((d) => typeof d === 'string')) {
    return data.details as string[];
  }
  return undefined;
}

function extractErrorCode(error: AxiosError<ApiResponse<unknown>>): string | undefined {
  const data = error.response?.data;
  if (!data || typeof data !== 'object') {
    return undefined;
  }
  if ('code' in data && typeof data.code === 'string' && data.code.length > 0) {
    return data.code;
  }
  const nested = (data as ApiResponse<{ code?: string }>).data;
  if (nested && typeof nested === 'object' && 'code' in nested && typeof nested.code === 'string') {
    return nested.code;
  }
  return undefined;
}

export function toApiError(error: unknown): ApiError {
  if (isApiError(error)) {
    return error;
  }
  if (axios.isAxiosError(error)) {
    const status = error.response?.status ?? 0;
    const typedError = error as AxiosError<ApiResponse<unknown>>;
    return new ApiError(
      extractErrorMessage(typedError),
      status,
      extractErrorCode(typedError),
      extractErrorDetails(typedError),
      typedError.response?.data
    );
  }
  if (error instanceof Error) {
    return new ApiError(error.message, 0);
  }
  return new ApiError('Request failed', 0);
}

let handlingUnauthorized = false;

async function handleUnauthorized(): Promise<void> {
  if (handlingUnauthorized || typeof window === 'undefined') {
    return;
  }
  handlingUnauthorized = true;
  try {
    const pathname = window.location.pathname;
    const pathWithSearch = `${pathname}${window.location.search}`;
    const skipForcedSignOut =
      isPublicBrowsePath(pathname) ||
      isPlatformOnboardingPath(pathWithSearch) ||
      pathname === '/login' ||
      pathname === '/register';

    if (skipForcedSignOut) {
      return;
    }

    const { useAuthStore } = await import('../store/auth');
    await useAuthStore.getState().logout();
    window.location.href = '/login';
  } catch {
    // Best-effort sign-out
  } finally {
    handlingUnauthorized = false;
  }
}

async function attachAuthHeader(config: InternalAxiosRequestConfig): Promise<InternalAxiosRequestConfig> {
  if (auth.currentUser) {
    try {
      const token = await auth.currentUser.getIdToken();
      config.headers.set('Authorization', `Bearer ${token}`);
    } catch (error) {
      console.error('Error getting ID token:', error);
    }
  }
  const impersonationHeaders = getImpersonationRequestHeaders();
  for (const [key, value] of Object.entries(impersonationHeaders)) {
    config.headers.set(key, value);
  }
  return config;
}

function createClient(baseURL: string, withAuth: boolean): AxiosInstance {
  const client = axios.create({
    baseURL,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (withAuth) {
    client.interceptors.request.use(attachAuthHeader);
  }

  client.interceptors.response.use(
    (response) => response,
    async (error: unknown) => {
      const apiError = toApiError(error);
      if (apiError.status === 401) {
        await handleUnauthorized();
      } else if (apiError.status === 403) {
        clearStaleImpersonationIfNeeded(apiError.message);
      } else if (apiError.status === 429 && typeof window !== 'undefined') {
        toast.error(apiError.message);
      }
      return Promise.reject(apiError);
    }
  );

  return client;
}

/** Authenticated API client — injects Firebase token on every request. */
export const apiClient = createClient(API_BASE_URL, true);

/** Public/unauthenticated API client (no Authorization header). */
export const publicApiClient = createClient(API_BASE_URL, false);

/** Typed GET returning unwrapped ApiResponse data. */
export async function apiGet<T>(
  url: string,
  config?: AxiosRequestConfig
): Promise<ApiResponse<T>> {
  const response = await apiClient.get<ApiResponse<T>>(url, config);
  return response.data;
}

export async function apiPost<T>(
  url: string,
  body?: unknown,
  config?: AxiosRequestConfig
): Promise<ApiResponse<T>> {
  const response = await apiClient.post<ApiResponse<T>>(url, body, config);
  return response.data;
}

export async function apiPut<T>(
  url: string,
  body?: unknown,
  config?: AxiosRequestConfig
): Promise<ApiResponse<T>> {
  const response = await apiClient.put<ApiResponse<T>>(url, body, config);
  return response.data;
}

export async function apiPatch<T>(
  url: string,
  body?: unknown,
  config?: AxiosRequestConfig
): Promise<ApiResponse<T>> {
  const response = await apiClient.patch<ApiResponse<T>>(url, body, config);
  return response.data;
}

export async function apiDelete<T>(
  url: string,
  config?: AxiosRequestConfig
): Promise<ApiResponse<T>> {
  const response = await apiClient.delete<ApiResponse<T>>(url, config);
  return response.data;
}

/** Public GET without auth header. */
export async function publicApiGet<T>(
  url: string,
  config?: AxiosRequestConfig
): Promise<ApiResponse<T>> {
  const response = await publicApiClient.get<ApiResponse<T>>(url, config);
  return response.data;
}
